/**
 * Offline Queue — Enqueue actions to be synced when online.
 */
import {
  addToQueue,
  getPendingActions,
  getFailedActions,
  updateQueueAction,
  removeFromQueue,
  clearCompletedActions,
  type OfflineAction,
} from './offlineDb';

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000; // 1s base for exponential backoff
const MAX_DELAY_MS = 60000; // 60s cap

/** Calculate delay with exponential backoff + jitter */
function getBackoffDelay(retries: number): number {
  const exponential = Math.min(BASE_DELAY_MS * 2 ** retries, MAX_DELAY_MS);
  const jitter = exponential * 0.5 * Math.random(); // 0–50% jitter
  return exponential + jitter;
}

/** Promise-based delay */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Mutex to prevent concurrent syncs */
let isSyncing = false;

// Generate simple UUID
function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Enqueue an action to be executed when online.
 */
export async function enqueueOfflineAction(
  action: Omit<OfflineAction, 'id' | 'createdAt' | 'status' | 'retries'>
): Promise<string> {
  const id = uuid();
  await addToQueue({
    ...action,
    id,
    createdAt: Date.now(),
    status: 'pending',
    retries: 0,
  });

  // Dispatch custom event so UI can react
  window.dispatchEvent(new CustomEvent('offline-queue-changed'));

  // If online, try to sync immediately
  if (navigator.onLine) {
    syncQueue().catch(console.warn);
  }

  return id;
}

/**
 * Process all pending + failed (retryable) actions.
 * Called automatically when coming back online.
 * Uses a mutex to prevent duplicate processing.
 */
export async function syncQueue(): Promise<{ synced: number; failed: number }> {
  // Mutex: skip if already syncing
  if (isSyncing) {
    console.info('[OfflineQueue] Sync already in progress, skipping');
    return { synced: 0, failed: 0 };
  }

  isSyncing = true;

  try {
    const { supabase } = await import('@/integrations/supabase/client');

    const pending = await getPendingActions();
    const retryable = (await getFailedActions()).filter((a) => a.retries < MAX_RETRIES);

    // Filter retryable items that have waited long enough (backoff)
    const now = Date.now();
    const readyRetryable = retryable.filter((a) => {
      const waitMs = getBackoffDelay(a.retries);
      // Use errorMessage timestamp marker or fall back to createdAt
      const lastAttemptAt = (a as any).lastAttemptAt ?? a.createdAt;
      return now - lastAttemptAt >= waitMs;
    });

    const toProcess = [...pending, ...readyRetryable].sort((a, b) => a.createdAt - b.createdAt);

    let synced = 0;
    let failed = 0;

    for (const action of toProcess) {
      await updateQueueAction(action.id, { status: 'syncing' });

      try {
        let result: { error: any } | undefined;

        const tbl = action.table as any;

        if (action.action === 'insert') {
          result = await supabase.from(tbl).insert(action.payload as any);
        } else if (action.action === 'update') {
          const payload = action.payload as Record<string, unknown>;
          const { id: rowId, ...rest } = payload;
          result = await supabase.from(tbl).update(rest as any).eq('id', rowId as string);
        } else if (action.action === 'upsert') {
          result = await supabase.from(tbl).upsert(action.payload as any);
        }

        if (result?.error) {
          throw new Error(result.error.message);
        }

        await removeFromQueue(action.id);
        synced++;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro desconhecido';
        await updateQueueAction(action.id, {
          status: 'failed',
          retries: action.retries + 1,
          errorMessage: msg,
          lastAttemptAt: Date.now(),
        } as any);
        failed++;
      }
    }

    // Clean up completed actions
    await clearCompletedActions();

    // Notify UI
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));

    return { synced, failed };
  } finally {
    isSyncing = false;
  }
}
